import { countBy, min, nonNullable, sortBy } from 'lib/array/arrayUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getPackageOptionType, getRoomTypeCapacity } from './tourUtils'
import { hasAvailableTourBundleRooms } from './tourBundleRoomAvailability'

/**
 * Returns the cheapest available option for the departure for the travellers given
 */
export function getDepartureCheapestOptionForTravellers(
  departure: App.Tours.TourV2OfferDeparture,
  travellers: App.Occupants,
  packageType?: string,
): App.Tours.TourV2OfferPurchasableOption | undefined {
  if (!hasAvailableTourBundleRooms(departure, 1)) return undefined
  const viableOptions = getDepartureOptionsAvailableForTravellers(departure, travellers, packageType)
  return min(viableOptions, option => (option.ltleBundleId ? option.bundledPrice : undefined) ?? (option.memberPrice || option.price))
}

/**
 * Returns all available options for the departure for the travellers given and room type optionally
 */
export function getDepartureOptionsAvailableForTravellers(
  departure: App.Tours.TourV2OfferDeparture,
  travellers: App.Occupants,
  packageType?: string,
): Array<App.Tours.TourV2OfferPurchasableOption> {
  const guestCount = sumUpOccupancies([travellers])
  return departure.options.filter(option => {
    if (departure.ltleBundleId && option.ltleBundleId !== departure.ltleBundleId) return false
    const fitPackageType = packageType ? getPackageOptionType(option.roomType) === packageType : true
    const minGuests = getRoomTypeCapacity(option.roomType)
    const fitsRoomSize = guestCount === minGuests
    const hasEnoughInventory = !!option.inventoryLeft
    return hasEnoughInventory && fitsRoomSize && fitPackageType
  })
}

interface TourRoomOptionView {
  room: App.Occupants
  cheapestOption: App.Tours.TourV2OfferPurchasableOption
  options: Array<App.Tours.TourV2OfferPurchasableOption>
}

/**
 * Returns a list of options that are available to for the set of travellers given
 * If there are no room combinations that fit all travellers, will return an empty list
 */
export function getDepartureOptionsForTravellers(
  departure: App.Tours.TourV2OfferDeparture,
  travellers: Array<App.Occupants>,
  packageType?: string,
): Array<TourRoomOptionView> {
  if (!hasAvailableTourBundleRooms(departure, travellers.length)) return []
  const options = travellers.map<TourRoomOptionView | undefined>(traveller => {
    const options = getDepartureOptionsAvailableForTravellers(departure, traveller, packageType)
    if (options.length) {
      return {
        options,
        cheapestOption: min(options, option => (option.ltleBundleId ? option.bundledPrice : undefined) ?? (option.memberPrice || option.price))!,
        room: traveller,
      }
    }
  })
  const definedOptions = nonNullable(options)
  if (options.length !== definedOptions.length) {
    // one of the traveller sets doesn't have an option available to it, so it's not available
    return []
  }

  // Key rooms by the option they'd actually be booked against, not by their list of viable
  // options - that list is a fresh array per room, so identity keying bucketed every room alone.
  const roomsRequiredPerOption = countBy(definedOptions, option => option.cheapestOption)
  if (roomsRequiredPerOption.entries().some(([option, roomsRequired]) => {
    return option.inventoryLeft < roomsRequired
  })) {
    return []
  }

  return definedOptions
}

export type DepartureSoldOutReason = 'sold-out' | 'travellers'

/**
 * Determines whether or not a list of departures are sold out
 * and gives the reason why they are sold out
 * @param departures The list of departures to check
 * @param travellers Optionally include travellers to check capacity
 * @returns Whether or not it's sold out and why
 */
export function areDeparturesSoldOut(
  departures: Array<App.Tours.TourV2OfferDeparture>,
  travellers?: Array<App.Occupants>,
): DepartureSoldOutReason | undefined {
  const soldOut = departures.every(dep => {
    if (travellers) {
      return getDepartureOptionsForTravellers(dep, travellers).length === 0
    }
    return dep.options.length === 0
  })
  let reason: DepartureSoldOutReason | undefined
  if (soldOut) {
    reason = departures.every(dep => dep.options.length === 0) ? 'sold-out' : 'travellers'
  }

  return reason
}

/**
 * Returns the list of departures that are available for the given date change
 * @param tourOffer
 * @param snapshot snapshot of the offer on purchase
 */
export function getEligibleDeparturesForDateChange(
  tourOffer?: App.Tours.TourV2Offer,
  snapshot?: App.Tours.TourV2OfferSnapshot,
): Array<App.Tours.TourV2OfferDeparture> {
  if (!snapshot || !tourOffer) {
    return []
  }
  const existingTourItemfkVariationId = Object.values(tourOffer.variations).find((variation) => variation.fkTourOptionId === snapshot.tourOptionId)?.id
  const departures = existingTourItemfkVariationId ? tourOffer.variations[existingTourItemfkVariationId].departures : []
  const travellers = snapshot.rooms.flatMap(room => room.occupant)
  const packageType = getPackageOptionType(snapshot.rooms[0].roomType)
  const availablesDepartures = departures.filter((departure: App.Tours.TourV2OfferDeparture) => !!getDepartureOptionsForTravellers(departure, travellers, packageType).length)
  return sortBy(availablesDepartures, departure => new Date(departure.startDate), 'asc')
}
